import React, { useState, useEffect, useCallback } from "react";
import {
  X, Megaphone, RefreshCw, MessageCircle, Copy, Check,
  Users, Crown, MoonStar, Sparkles, History, Send,
} from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";

const SEGMENT_META = {
  ALL:      { label: "All customers", icon: Users,    color: "text-slate-600",  bg: "bg-slate-50 border-slate-200" },
  VIP:      { label: "VIP / frequent", icon: Crown,   color: "text-amber-600",  bg: "bg-amber-50 border-amber-200" },
  INACTIVE: { label: "Win-back",       icon: MoonStar, color: "text-violet-600", bg: "bg-violet-50 border-violet-200" },
  NEW:      { label: "New customers",  icon: Sparkles, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
};

const TEMPLATES = [
  { id: "offer",    label: "Weekend offer",  text: "Hi {name}! 🍽️ This weekend only — flat 20% off on your favourite dishes. Visit us or order now!" },
  { id: "winback",  label: "We miss you",    text: "Hi {name}, it's been a while! 😊 Come back this week and enjoy 15% off your next meal — on us." },
  { id: "new",      label: "Welcome",        text: "Hi {name}, thanks for visiting us! 🙏 Show this message on your next visit for a free dessert." },
  { id: "festival", label: "Festival special", text: "Hi {name}! 🎉 Celebrate with our special festive menu this week. Book your table today!" },
];

export default function MarketingPanel({ onClose }) {
  const [segments, setSegments] = useState([]);
  const [rules, setRules] = useState({});
  const [activeSegment, setActiveSegment] = useState("INACTIVE");
  const [customers, setCustomers] = useState([]);
  const [message, setMessage] = useState(TEMPLATES[1].text);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [sentTo, setSentTo] = useState({}); // mobile → true (this session)

  const fetchSegments = useCallback(async () => {
    try {
      const res = await api.get("/marketing/segments");
      setSegments(res.data.segments || []);
      setRules(res.data.rules || {});
    } catch {
      toast.error("Failed to load segments");
    }
  }, []);

  const fetchCustomers = useCallback(async (seg) => {
    setLoading(true);
    try {
      const res = await api.get(`/marketing/customers?segment=${seg}`);
      setCustomers(res.data.customers || []);
    } catch {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSegments(); }, [fetchSegments]);
  useEffect(() => { fetchCustomers(activeSegment); }, [activeSegment, fetchCustomers]);

  const personalise = (text, name) => text.replace(/\{name\}/g, name || "there");

  const waLink = (c) =>
    `https://wa.me/91${c.mobile}?text=${encodeURIComponent(personalise(message, c.name))}`;

  const openWhatsApp = (c) => {
    window.open(waLink(c), "_blank", "noopener");
    setSentTo(prev => ({ ...prev, [c.mobile]: true }));
  };

  const copyNumbers = () => {
    const nums = customers.map(c => c.mobile).join(", ");
    navigator.clipboard.writeText(nums).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(`${customers.length} numbers copied — paste into a broadcast list`);
    });
  };

  const logCampaign = async (channel) => {
    try {
      await api.post("/marketing/campaigns", {
        segment: activeSegment,
        channel,
        message,
        recipientCount: customers.length,
      });
      toast.success("Campaign logged to history");
      if (showHistory) loadHistory();
    } catch {
      toast.error("Failed to log campaign");
    }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get("/marketing/campaigns");
      setCampaigns(res.data.campaigns || []);
    } catch { /* silent */ }
  };

  const toggleHistory = () => {
    if (!showHistory) loadHistory();
    setShowHistory(v => !v);
  };

  const daysAgo = (d) => {
    if (!d) return "never";
    const diff = Math.floor((Date.now() - new Date(d)) / 86400000);
    return diff === 0 ? "today" : `${diff}d ago`;
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-pink-500/15 border border-pink-500/25 flex items-center justify-center">
              <Megaphone size={15} className="text-pink-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Marketing</h2>
              <p className="text-[10px] text-slate-400">WhatsApp & SMS campaigns · customer segments</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleHistory}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl transition-colors ${showHistory ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              <History size={12} /> History
            </button>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
              <X size={14} />
            </button>
          </div>
        </div>

        {showHistory ? (
          /* ── Campaign history ── */
          <div className="overflow-y-auto flex-1 px-5 py-4 space-y-2">
            {campaigns.length === 0 ? (
              <p className="text-center text-xs text-slate-400 py-10">No campaigns yet</p>
            ) : campaigns.map(c => (
              <div key={c._id} className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${SEGMENT_META[c.segment]?.bg} ${SEGMENT_META[c.segment]?.color}`}>
                      {SEGMENT_META[c.segment]?.label || c.segment}
                    </span>
                    <span className="text-[10px] font-bold text-slate-500">{c.channel}</span>
                    <span className="text-[10px] text-slate-400">→ {c.recipientCount} customers</span>
                  </div>
                  <span className="text-[10px] text-slate-400">{new Date(c.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <p className="text-xs text-slate-600 mt-1.5">{c.message}</p>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Segment cards */}
            <div className="grid grid-cols-4 gap-2 px-5 py-3 border-b border-slate-100">
              {segments.map(s => {
                const meta = SEGMENT_META[s.id];
                const Icon = meta.icon;
                const active = activeSegment === s.id;
                return (
                  <button key={s.id} onClick={() => setActiveSegment(s.id)}
                    className={`rounded-xl border p-2.5 text-left transition-all ${active ? `${meta.bg} ring-2 ring-offset-1 ring-pink-400` : "bg-white border-slate-200 hover:border-slate-300"}`}>
                    <Icon size={14} className={meta.color} />
                    <p className="text-lg font-black text-slate-800 mt-1 leading-none">{s.count}</p>
                    <p className="text-[9px] font-bold text-slate-500 mt-0.5">{meta.label}</p>
                    {rules[s.id] && <p className="text-[8px] text-slate-400">{rules[s.id]}</p>}
                  </button>
                );
              })}
            </div>

            {/* Composer */}
            <div className="px-5 py-3 border-b border-slate-100 space-y-2">
              <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
                {TEMPLATES.map(t => (
                  <button key={t.id} onClick={() => setMessage(t.text)}
                    className="shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                    {t.label}
                  </button>
                ))}
              </div>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={2}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-pink-400 resize-none"
                placeholder="Your message… use {name} to personalise"
              />
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-slate-400">
                  <code className="bg-slate-100 px-1 rounded">{"{name}"}</code> is replaced with each customer's name
                </p>
                <div className="flex gap-2">
                  <button onClick={copyNumbers} disabled={customers.length === 0}
                    className="flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40">
                    {copied ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
                    Copy {customers.length} numbers
                  </button>
                  <button onClick={() => logCampaign("WHATSAPP")} disabled={customers.length === 0}
                    className="flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1.5 rounded-lg bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-40">
                    <Send size={11} /> Log campaign
                  </button>
                </div>
              </div>
            </div>

            {/* Customer list */}
            <div className="overflow-y-auto flex-1 px-5 py-3 space-y-1.5">
              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <RefreshCw size={18} className="animate-spin text-slate-400" />
                </div>
              ) : customers.length === 0 ? (
                <p className="text-center text-xs text-slate-400 py-10">No customers in this segment</p>
              ) : customers.map(c => (
                <div key={c.mobile} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-800 truncate">{c.name}</p>
                    <p className="text-[10px] text-slate-400">
                      {c.mobile} · {c.totalBills || 0} visits · last {daysAgo(c.lastVisit)}
                    </p>
                  </div>
                  <button
                    onClick={() => openWhatsApp(c)}
                    className={`shrink-0 flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-lg transition-colors ${sentTo[c.mobile] ? "bg-emerald-100 text-emerald-600" : "bg-[#25D366]/10 text-[#128C7E] hover:bg-[#25D366]/20 border border-[#25D366]/30"}`}
                  >
                    <MessageCircle size={11} />
                    {sentTo[c.mobile] ? "Sent" : "WhatsApp"}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <p className="text-[10px] text-slate-400">
            WhatsApp opens with a pre-filled personalised message — free, no gateway needed. Use "Copy numbers" for broadcast lists.
          </p>
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 shrink-0">Close</button>
        </div>
      </div>
    </div>
  );
}
